import React, { useState, useEffect, useContext } from "react";
import { withStyles } from "@material-ui/core";
import LoadingIndicator from "../../../../../../Component/LoadingIndicator";
import ModalDialog from "../ModalDialog";
import ModalDialogContext from "../../../../../../Context/ModalDialog/ModalDialogContext";
import { styles } from "./styles";
import CountryBasicDataTable from "./CountryBasicDataTable";
import CountryForm from "./CountryForm";
import { recordTypes } from "../../../../../../API/config";
import {
  getCountriesWithContacts,
  addCountry,
  updateCountry,
} from "../../../../../../API/Country";
import { useTranslation } from "react-i18next";

const validateEmail = (email) => {
  const re =
    /^((([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})(;)?))+$/;
  return re.test(String(email).toLowerCase());
};

const CountryBasicTable = (props) => {
  const { classes, hasPermission } = props;
  const [countries, setCountries] = useState([]);
  const [loadCountries, setLoadCountries] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [country, setCountry] = useState();
  const [addingNew, setAddingNew] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [canEdit, setCanEdit] = useState();
  const [canAdd, setCanAdd] = useState();

  const { t } = useTranslation();

  const { showMessage, showErrorMessage, showLoadingMessage } =
    useContext(ModalDialogContext);

  useEffect(() => {
    const evalPermissions = async () => {
      const canEdit = await hasPermission(
        recordTypes.country.name,
        recordTypes.country.actions.update.name,
      );
      setCanEdit(canEdit);
      const canAdd = await hasPermission(
        recordTypes.country.name,
        recordTypes.country.actions.create.name,
      );
      setCanAdd(canAdd);
    };

    evalPermissions();
  }, []);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoading(true);
        const result = await getCountriesWithContacts();
        const { data = [] } = result;
        setCountries(data);
        setLoading(false);
      } catch (error) {
        showErrorMessage({ error });
        setLoading(false);
      }
    };
    loadCountries();
  }, [loadCountries]);

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const openAddDialog = () => {
    setCountry({});
    setFormErrors({});
    setAddingNew(true);
    setIsEditDialogOpen(true);
  };

  const openUpdateDialog = (e, country) => {
    setCountry(country);
    setAddingNew(false);
    setFormErrors({});
    setIsEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setCountry({});
    setFormErrors({});
    setIsEditDialogOpen(false);
  };

  const onCountryChange = (value, name) => {
    setCountry((c) => {
      c[name] = value;
      return c;
    });
  };

  const isInvalidForm = () => {
    const errors = {};
    setFormErrors(errors);

    let hasError = false;
    if (!country.name || typeof country.name !== "string") {
      errors["name"] = t("messages.country.countryNameError");
      hasError = true;
    }
    if (
      !country.iso_code_2 ||
      typeof country.iso_code_2 !== "string" ||
      country.iso_code_2.length !== 2
    ) {
      errors["iso_code_2"] = t("messages.country.countryIsoCode2Error");
      hasError = true;
    }
    if (
      !country.iso_code_3 ||
      typeof country.iso_code_3 !== "string" ||
      country.iso_code_3.length !== 3
    ) {
      errors["iso_code_3"] = t("messages.country.countryIsoCode3Error");
      hasError = true;
    }

    if (
      country.mlro_contact_emails &&
      (typeof country.mlro_contact_emails !== "string" ||
        !validateEmail(country.mlro_contact_emails))
    ) {
      errors["mlro_contact_emails"] = t(
        "messages.country.mlroContactEmailsError",
      );
      hasError = true;
    }

    if (
      country.mlro_delegate_emails &&
      (typeof country.mlro_delegate_emails !== "string" ||
        !validateEmail(country.mlro_delegate_emails))
    ) {
      errors["mlro_delegate_emails"] = t(
        "messages.country.mlroDelegateEmailsError",
      );
      hasError = true;
    }

    if (hasError) {
      setFormErrors(errors);
    }

    return hasError;
  };

  const submitCountry = async () => {
    try {
      setFormErrors({});
      const invalidForm = isInvalidForm();
      if (invalidForm) return;
      showLoadingMessage();
      const method = addingNew ? addCountry : updateCountry;

      const newCountry = {
        ...(country || {}),
        parent_id: country.parent_id,
        jll_covered: country.jll_covered,
      };
      await method(newCountry);
      closeEditDialog();
      setLoadCountries(true);
      showMessage({
        message: addingNew
          ? t("messages.country.countryCreated")
          : t("messages.country.countryUpdated"),
      });
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  return (
    <>
      {loading ? (
        renderLoadingIndicator()
      ) : (
        <div className={classes.tableContainer}>
          <CountryBasicDataTable
            loading={loading}
            countries={countries}
            onNewAction={openAddDialog}
            onUpdateRecord={openUpdateDialog}
            canAdd={canAdd}
            canEdit={canEdit}
          />
        </div>
      )}
      <ModalDialog
        open={isEditDialogOpen}
        onClose={closeEditDialog}
        onSubmit={submitCountry}
        title={
          addingNew
            ? t("labels.country.basicInformation.addNewCountry")
            : t("labels.country.basicInformation.updateCountry")
        }
      >
        <CountryForm
          onFieldChange={onCountryChange}
          country={country}
          formErrors={formErrors}
        />
      </ModalDialog>
    </>
  );
};

export default withStyles(styles, { withTheme: true })(CountryBasicTable);
